// account-cabinet-self-progress-history-tabs.simple.js
import axios from 'axios';

const DATA_ATTR = 'data-account-cabinet-self-progress-history';
const CLASS_PREFIX = 'account-cabinet-self-progress-history';

export function initPersonalAccountTabProgressHistory($container) {
    const $tplMain = document.querySelector(`[${DATA_ATTR}-template-main]`);
    const $tplDate = document.querySelector(`[${DATA_ATTR}-date-template]`);
    const $tplCard = document.querySelector(`[${DATA_ATTR}-card-template]`);

    if (!$tplMain || !$tplDate || !$tplCard) {
        console.error('History templates not found in DOM.');
        return;
    }

    // state
    let history = { data: {}, hasMore: false };
    let page = 1;
    let isLoading = false;

    // root elements (will be created on first render)
    let $root = null;
    let $content = null;
    let $showMoreBtn = null;

    init();

    function init() {
        renderShell();
        fetchHistory(page);
    }

    // --- Fetching ---
    function fetchHistory(pageNum = 1) {
        if (isLoading) return;
        isLoading = true;
        toggleShowMoreLoading(true);

        axios.get('/api/exercises/user-activity', { params: { page: pageNum } })
            .then(({ data: resp }) => {

                mergeHistory(resp);
                renderContent();
                document.dispatchEvent(new CustomEvent('account-cabinet:render-complete', {bubbles: true}));
            })
            .catch((err) => {
                console.error('Error fetching user activity:', err);
            })
            .finally(() => {
                isLoading = false;
                toggleShowMoreLoading(false);
            });
    }

    // Render main shell from template and prepare placeholders
    function renderShell() {
        const $frag = $tplMain.content.cloneNode(true);
        $root = $frag.querySelector(`[${DATA_ATTR}]`) || $frag.querySelector(`.${CLASS_PREFIX}`);
        $content = $frag.querySelector(`[${DATA_ATTR}-content]`) || $frag.querySelector(`.${CLASS_PREFIX}__content`);

        // create "Показать ещё" button
        $showMoreBtn = document.createElement('button');
        $showMoreBtn.type = 'button';
        $showMoreBtn.className = `${CLASS_PREFIX}__show-more`;
        $showMoreBtn.textContent = 'Показать ещё';
        $showMoreBtn.style.margin = '12px auto 0';
        $showMoreBtn.style.display = 'block';
        $showMoreBtn.addEventListener('click', onShowMoreClick);

        $container.appendChild($frag);
    }

    function onShowMoreClick() {
        if (isLoading) return;
        page += 1;
        fetchHistory(page);
    }

    function toggleShowMoreLoading(loading) {
        if (!$showMoreBtn) return;
        $showMoreBtn.disabled = loading;
        $showMoreBtn.textContent = loading ? 'Загрузка...' : 'Показать ещё';
    }

    // --- Data merge & helpers ---
    function mergeHistory(newResp) {
        if (!newResp || typeof newResp !== 'object') return;
        const newData = newResp.data || {};
        const newHasMore = !!newResp.hasMore;

        for (const [dateKey, items] of Object.entries(newData)) {
            if (!Array.isArray(items)) continue;
            if (!history.data[dateKey]) history.data[dateKey] = [];
            const existing = history.data[dateKey];
            const existingSignatures = new Set(existing.map(e => signature(e)));
            for (const it of items) {
                if (!existingSignatures.has(signature(it))) {
                    existing.push(it);
                }
            }
            // sort descending by last_viewed_at
            history.data[dateKey].sort((a, b) => {
                const ta = Date.parse(a.last_viewed_at || 0);
                const tb = Date.parse(b.last_viewed_at || 0);
                return tb - ta;
            });
        }

        history.hasMore = newHasMore;
    }

    function signature(item) {
        return `${item.title}||${item.last_viewed_at}||${item.value}`;
    }

    // --- Rendering ---
    function renderContent() {
        if (!$content) return;
        $content.innerHTML = '';

        const dateEntries = Object.entries(history.data || {});
        if (dateEntries.length === 0) {
            $content.innerHTML = `<div class="${CLASS_PREFIX}__empty">История пуста</div>`;
            return;
        }

        for (const [dateKey, items] of dateEntries) {
            const $dateBlock = renderDateBlock(dateKey, items);
            $content.appendChild($dateBlock);
        }

        if (history.hasMore) {
            if (!$content.contains($showMoreBtn)) {
                $content.appendChild($showMoreBtn);
            }
            $showMoreBtn.style.display = 'block';
        } else {
            if ($content.contains($showMoreBtn)) {
                $showMoreBtn.style.display = 'none';
            }
        }
    }

    function renderDateBlock(dateLabel, items = []) {
        const $frag = $tplDate.content.cloneNode(true);
        const $label = $frag.querySelector(`[${DATA_ATTR}-month]`) || $frag.querySelector(`.${CLASS_PREFIX}__label`);
        const $cards = $frag.querySelector(`[${DATA_ATTR}-cards]`) || $frag.querySelector(`.${CLASS_PREFIX}__cards`);
        if ($label) $label.textContent = dateLabel;

        for (const item of items) {
            const $card = renderCard(item);
            $cards.appendChild($card);
        }

        const $wrapper = document.createElement('div');
        $wrapper.appendChild($frag);
        return $wrapper;
    }

    function renderCard(item) {
        const $frag = $tplCard.content.cloneNode(true);
        const $cardRoot = $frag.querySelector(`[${DATA_ATTR}-card]`) || $frag.querySelector(`.${CLASS_PREFIX}__card`);

        const $tagsContainer = $frag.querySelector(`[${DATA_ATTR}-tags]`) || $frag.querySelector(`.${CLASS_PREFIX}__card-list`);
        const $time = $frag.querySelector(`[${DATA_ATTR}-datetime]`) || $frag.querySelector(`.${CLASS_PREFIX}__card-time`);
        const $title = $frag.querySelector(`[${DATA_ATTR}-title]`) || $frag.querySelector(`.${CLASS_PREFIX}__card-title`);
        const $label = $frag.querySelector(`.${CLASS_PREFIX}__card-label`);
        const $type = $frag.querySelector(`[${DATA_ATTR}-type]`) || $frag.querySelector(`.${CLASS_PREFIX}__task-label`);
        const $progress = $frag.querySelector(`[${DATA_ATTR}-progress]`) || $frag.querySelector(`.${CLASS_PREFIX}__task-progress`);
        const $timeNumber = $frag.querySelector(`[${DATA_ATTR}-time]`) || $frag.querySelector(`.${CLASS_PREFIX}__task-number`);

        if ($title) $title.textContent = item.title || '—';

        if ($tagsContainer) {
            $tagsContainer.innerHTML = '';
            if (item.tags) {
                for (const tag of item.tags) {
                    const $tag = document.createElement('div');
                    $tag.setAttribute(`${DATA_ATTR}-tag`, '');
                    $tag.className = `${CLASS_PREFIX}__card-list-tag`;
                    $tag.textContent = tag;
                    $tagsContainer.appendChild($tag);
                }
            }
        }

        if ($time) {
            $time.textContent = formatTime(item.last_viewed_at) || '';
        }

        if ($label) {
            $label.textContent = item.category || 'Упражнения';
        }

        if ($type) {
            $type.textContent = item.type || '';
        }

        if ($timeNumber) {
            $timeNumber.textContent = item.duration || '';
        }

        return $cardRoot ? $cardRoot : $frag;
    }

    // --- Utilities ---
    function formatTime(iso) {
        try {
            if (!iso) return '';
            const dt = new Date(iso);
            if (Number.isNaN(dt.getTime())) return '';
            const hh = String(dt.getHours()).padStart(2, '0');
            const mm = String(dt.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
        } catch (e) {
            return '';
        }
    }

    function formatMinutesToReadable(minutes) {
        if (typeof minutes !== 'number' || Number.isNaN(minutes)) return String(minutes || '');
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        if (h > 0) {
            return `${h} ч. ${m} м.`;
        }
        return `${m} м.`;
    }
}
